import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight, FaEye, FaSearchMinus, FaSearchPlus, FaTimes } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button, Text } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../../assets/logo_completo.png';
import Spinner from '../../../components/bootstrap/Spinner';
import { menuRoutes } from "../../../router/menu";
import { ContractService } from "../../../services/contract/contractService";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PendingSignatureDocument = 'contract' | 'direct_debit_mandate';
const SIGNATURE_CANVAS_MAX_WIDTH = 375;
const SIGNATURE_CANVAS_HEIGHT = 150;
const SIGNATURE_SCREEN_HORIZONTAL_PADDING = 48;

const SignContractMobile: FC = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { hash } = useParams<{ hash: string }>();

    const [signatureHash, setSignatureHash] = useState<string | undefined>();
    const [showSignature, setShowSignature] = useState(false);
    const [contractData, setContractData] = useState<any | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [contractPdfBlob, setContractPdfBlob] = useState<string | null>(null);
    const [directDebitPdfBlob, setDirectDebitPdfBlob] = useState<string | null>(null);
    const [activePreviewDocument, setActivePreviewDocument] = useState<PendingSignatureDocument>('contract');
    const [loadedPreviewByType, setLoadedPreviewByType] = useState<Record<PendingSignatureDocument, boolean>>({
        contract: false,
        direct_debit_mandate: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [signingDocuments, setSigningDocuments] = useState(false);
    const [safeTop, setSafeTop] = useState(0);
    const [signatureCanvasWidth, setSignatureCanvasWidth] = useState(SIGNATURE_CANVAS_MAX_WIDTH);

    const contractSignatureRef = useRef<SignatureCanvas>(null);
    const directDebitSignatureRef = useRef<SignatureCanvas>(null);
    const pdfContainerRef = useRef<HTMLDivElement>(null);

    const pendingDocuments = useMemo<PendingSignatureDocument[]>(
        () => (contractData?.pendingSignatureDocuments ?? []) as PendingSignatureDocument[],
        [contractData]
    );
    const signatureSource = useMemo(() => {
        const source = new URLSearchParams(location.search).get('signatureSource')?.toLowerCase();
        return source === 'email' || source === 'sms' ? source : undefined;
    }, [location.search]);

    const pendingDocumentsCount = contractData?.pendingSignatureDocumentsCount ?? pendingDocuments.length;
    const hasContractPending = pendingDocuments.includes('contract');
    const hasDirectDebitPending = pendingDocuments.includes('direct_debit_mandate');
    const currentPdfBlob = activePreviewDocument === 'contract' ? contractPdfBlob : directDebitPdfBlob;
    const currentDocumentLoaded = loadedPreviewByType[activePreviewDocument];

    const goToSignedPage = (contract: any, contractIdFromResponse?: string) => {
        const contractId = contractIdFromResponse ?? contract?.id;

        if (!contractId) {
            navigate(menuRoutes.contract_public.error);
            return;
        }

        const requirements = (contract?.signatureRequirements ?? []) as PendingSignatureDocument[];

        if (requirements.includes('direct_debit_mandate')) {
            navigate(menuRoutes.direct_debit_mandate.signed.split(':')[0] + contractId);
            return;
        }

        navigate(menuRoutes.contract_public.signed.split(':')[0] + contractId);
    };

    const bufferToDataUrl = (buffer: ArrayBuffer): Promise<string> => {
        return new Promise((resolve, reject) => {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result ?? '') as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const handleObtainContractInfo = async (currentHash: string) => {
        try {
            const response = await (await (new ContractService()).findContractByHash(currentHash, null)).getResponseData();

            if (!response?.data) {
                throw new Error('No se ha encontrado el contrato');
            }

            setContractData(response.data);
        } catch (error) {
            toast.error('Hubo un problema al obtener la información del contrato.');
            navigate(menuRoutes.contract_public.error);
        }
    };

    const handlePreviewDocument = async (documentType: PendingSignatureDocument) => {
        if (!signatureHash) {
            return;
        }

        if (documentType === 'contract' && contractPdfBlob) {
            return;
        }

        if (documentType === 'direct_debit_mandate' && directDebitPdfBlob) {
            return;
        }

        try {
            const service = new ContractService();
            const response = documentType === 'contract'
                ? await service.visualizeContractPdf(signatureHash, undefined)
                : await service.visulizeDirectDebitPdf(signatureHash, undefined);

            if (!response?.data) {
                throw new Error('No se ha podido cargar el documento');
            }

            const dataUrl = await bufferToDataUrl(response.data);

            if (documentType === 'contract') {
                setContractPdfBlob(dataUrl);
            } else {
                setDirectDebitPdfBlob(dataUrl);
            }

            setLoadedPreviewByType((previous) => ({
                ...previous,
                [documentType]: true,
            }));
        } catch (error) {
            toast.error('No se ha podido cargar la previsualización del documento.');
        }
    };

    const handleSignPendingDocuments = async () => {
        if (!signatureHash) {
            toast.error('Hash inválido o nulo');
            return;
        }

        if (pendingDocumentsCount <= 0) {
            goToSignedPage(contractData);
            return;
        }

        if (hasContractPending && contractSignatureRef.current?.isEmpty()) {
            toast.error('Debe firmar el contrato para continuar.');
            return;
        }

        if (hasDirectDebitPending && directDebitSignatureRef.current?.isEmpty()) {
            toast.error('Debe firmar el mandato de domiciliación para continuar.');
            return;
        }

        const signatureContract = hasContractPending ? contractSignatureRef.current?.toDataURL() : undefined;
        const signatureDirectDebitMandate = hasDirectDebitPending ? directDebitSignatureRef.current?.toDataURL() : undefined;

        setIsLoading(true);
        setSigningDocuments(true);

        try {
            const response = await (await (new ContractService()).signContractDocuments(signatureHash, {
                signatureContract,
                signatureDirectDebitMandate,
                signatureSource,
            })).getResponseData();

            if (!response?.success) {
                toast.error(response?.message ?? 'No se han podido firmar los documentos');
                return;
            }

            toast.success(response?.message ?? 'Documentos firmados correctamente');
            goToSignedPage(contractData, response?.data?.contractId);
        } catch (error) {
            toast.error('Error al firmar los documentos');
        } finally {
            setIsLoading(false);
            setSigningDocuments(false);
        }
    };

    const onDocumentLoadSuccess = (pdf: any) => {
        setNumPages(pdf.numPages);
        if (pdfContainerRef.current) {
            pdf.getPage(1).then((page: any) => {
                const viewport = page.getViewport({ scale: 1.0 });
                const scale = ((pdfContainerRef.current?.clientWidth ?? 0) - 32) / viewport.width;
                setZoomLevel(scale);
            });
        }
    };

    useEffect(() => {
        const updateSafeArea = () => {
            setSafeTop(window.visualViewport?.offsetTop || 0);
        };

        window.visualViewport?.addEventListener("resize", updateSafeArea);
        updateSafeArea();

        return () => {
            window.visualViewport?.removeEventListener("resize", updateSafeArea);
        };
    }, []);

    useEffect(() => {
        const updateSignatureCanvasWidth = () => {
            const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
            const availableWidth = Math.floor(viewportWidth - SIGNATURE_SCREEN_HORIZONTAL_PADDING);

            setSignatureCanvasWidth(Math.min(SIGNATURE_CANVAS_MAX_WIDTH, Math.max(260, availableWidth)));
        };

        window.addEventListener('resize', updateSignatureCanvasWidth);
        window.visualViewport?.addEventListener('resize', updateSignatureCanvasWidth);
        updateSignatureCanvasWidth();

        return () => {
            window.removeEventListener('resize', updateSignatureCanvasWidth);
            window.visualViewport?.removeEventListener('resize', updateSignatureCanvasWidth);
        };
    }, []);

    useEffect(() => {
        if (!hash) {
            navigate(menuRoutes.contract_public.error);
            return;
        }

        setSignatureHash(hash);
        handleObtainContractInfo(hash);
    }, [hash]);

    useEffect(() => {
        if (!contractData) {
            return;
        }

        if (pendingDocuments.length === 0) {
            goToSignedPage(contractData);
            return;
        }

        if (!pendingDocuments.includes(activePreviewDocument)) {
            setActivePreviewDocument(pendingDocuments[0]);
            setPageNumber(1);
        }
    }, [contractData, pendingDocuments.join(','), activePreviewDocument]);

    useEffect(() => {
        if (!pendingDocuments.includes(activePreviewDocument)) {
            return;
        }

        handlePreviewDocument(activePreviewDocument);
    }, [activePreviewDocument, signatureHash, pendingDocuments.join(',')]);

    return (
        <div className="relative min-h-screen bg-white overflow-x-hidden">
            <div className="fixed top-4 right-4 z-50 flex items-center space-x-2">
                <button
                    onClick={() => setShowModal(true)}
                    className="p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700"
                    title="Ver documentos"
                >
                    <FaEye size={20} />
                </button>
                <span className="text-gray-700 text-sm font-medium bg-gray-100 px-3 py-1 rounded-lg shadow-md">
                    Previsualiza tus documentos aquí
                </span>
            </div>

            <div className="w-full min-h-[100dvh] grid grid-cols-1 overflow-y-auto overflow-x-hidden">
                <div className="mx-auto w-full max-w-md px-4 pt-20 pb-10 flex flex-col justify-start">
                    <div className="text-center mb-5">
                        <img src={fullLogo} className="h-10 mx-auto" alt="Logo de Attiva" />
                        <Text className="text-2xl font-bold mt-4">Firma de documentos</Text>
                    </div>

                    <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        Tienes {pendingDocumentsCount} documento{pendingDocumentsCount === 1 ? '' : 's'} pendiente{pendingDocumentsCount === 1 ? '' : 's'} por firmar.
                    </div>

                    {!showSignature && (
                        <Button type="button" onClick={() => setShowSignature(true)} className="w-full mt-6" size="lg">
                            Firmar documentos pendientes
                        </Button>
                    )}

                    {showSignature && !signingDocuments && (
                        <div className="space-y-5 mt-5">
                            {hasContractPending && (
                                <div className="border border-gray-300 rounded-md p-2 overflow-hidden">
                                    <p className="mb-2 text-sm font-medium text-gray-700">Firma del contrato</p>
                                    <SignatureCanvas
                                        ref={contractSignatureRef}
                                        canvasProps={{
                                            width: signatureCanvasWidth,
                                            height: SIGNATURE_CANVAS_HEIGHT,
                                            className: 'signature-canvas block max-w-full touch-none',
                                        }}
                                    />
                                    <div className="mt-2 flex justify-end">
                                        <Button
                                            type="button"
                                            onClick={() => contractSignatureRef.current?.clear()}
                                            variant="outline"
                                        >
                                            Limpiar
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {hasDirectDebitPending && (
                                <div className="border border-gray-300 rounded-md p-2 overflow-hidden">
                                    <p className="mb-2 text-sm font-medium text-gray-700">Firma del mandato de domiciliación</p>
                                    <SignatureCanvas
                                        ref={directDebitSignatureRef}
                                        canvasProps={{
                                            width: signatureCanvasWidth,
                                            height: SIGNATURE_CANVAS_HEIGHT,
                                            className: 'signature-canvas block max-w-full touch-none',
                                        }}
                                    />
                                    <div className="mt-2 flex justify-end">
                                        <Button
                                            type="button"
                                            onClick={() => directDebitSignatureRef.current?.clear()}
                                            variant="outline"
                                        >
                                            Limpiar
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <Button
                                type="button"
                                onClick={handleSignPendingDocuments}
                                isLoading={isLoading}
                                disabled={isLoading}
                                className="w-full"
                            >
                                Firmar y enviar documentos
                            </Button>
                        </div>
                    )}

                    {signingDocuments && (
                        <div className="flex flex-col justify-center items-center text-center">
                            <DotLottieReact
                                src="https://lottie.host/e7d7459a-de1c-4374-9631-3e03d9d81b11/S0RljqPaCz.lottie"
                                loop={false}
                                autoplay
                                speed={0.6}
                                className="mb-4"
                            />
                            <p className="text-gray-600">Firmando tus documentos...</p>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white w-screen h-screen overflow-hidden relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute top-2 right-2 p-2 text-gray-600 hover:text-gray-900 z-10"
                            style={{ top: `${safeTop + 16}px` }}
                        >
                            <FaTimes size={20} />
                        </button>

                        <div className="h-[100dvh] flex flex-col overflow-hidden">
                            <h2 className="text-xl font-semibold my-2 text-center">Previsualización de documentos</h2>

                            <div className="mx-auto mb-3 flex items-center gap-2">
                                {hasContractPending && (
                                    <Button
                                        variant={activePreviewDocument === 'contract' ? 'solid' : 'outline'}
                                        onClick={() => {
                                            setActivePreviewDocument('contract');
                                            setPageNumber(1);
                                        }}
                                    >
                                        Contrato
                                    </Button>
                                )}

                                {hasDirectDebitPending && (
                                    <Button
                                        variant={activePreviewDocument === 'direct_debit_mandate' ? 'solid' : 'outline'}
                                        onClick={() => {
                                            setActivePreviewDocument('direct_debit_mandate');
                                            setPageNumber(1);
                                        }}
                                    >
                                        Domiciliación
                                    </Button>
                                )}
                            </div>

                            <div ref={pdfContainerRef} className="flex-grow overflow-auto w-full">
                                <div className="w-full flex justify-center">
                                    {currentPdfBlob && currentDocumentLoaded ? (
                                        <Document
                                            file={currentPdfBlob}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                            loading={<Spinner />}
                                        >
                                            <Page
                                                pageNumber={pageNumber}
                                                renderMode="canvas"
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                                scale={zoomLevel}
                                            />
                                        </Document>
                                    ) : (
                                        <div className="flex justify-center items-center h-full">
                                            <Spinner />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 flex justify-between items-center px-3 pb-3">
                                <Button
                                    onClick={() => setPageNumber((prevPage) => Math.max(prevPage - 1, 1))}
                                    disabled={pageNumber <= 1}
                                >
                                    <FaArrowLeft />
                                </Button>

                                <div className="flex items-center space-x-2">
                                    <Button onClick={() => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5))}>
                                        <FaSearchMinus />
                                    </Button>
                                    <span className="text-gray-600">Zoom: {(zoomLevel * 100).toFixed(0)}%</span>
                                    <Button onClick={() => setZoomLevel((prev) => Math.min(prev + 0.1, 2.0))}>
                                        <FaSearchPlus />
                                    </Button>
                                </div>

                                <Button
                                    onClick={() => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1))}
                                    disabled={pageNumber >= (numPages || 1)}
                                >
                                    <FaArrowRight />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignContractMobile;
